import React, {useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'
import DatePicker from 'react-datepicker'
import {format} from 'date-fns'
import tr from 'date-fns/locale/tr'
import {UiIcon} from './UiIcon'
import './EducationDatePicker.scss'

type Props = {
  id: string
  selected: Date | null
  onChange: (date: Date | null) => void
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  monthOnly?: boolean
}

export function EducationDatePicker({id, selected, onChange, minDate, maxDate, disabled, monthOnly = false}: Props) {
  const field = useRef<HTMLDivElement>(null)
  const picker = useRef<DatePicker>(null)
  // Keep the calendar within a modal's focus trap, outside its scrolling/clipped body.
  const CalendarPortal = useCallback(({children}: {children?: React.ReactNode}) =>
    createPortal(children, field.current?.closest('.modal') || document.body), [])
  const today = new Date()
  const currentDate = monthOnly ? new Date(today.getFullYear(), today.getMonth(), 1) : today
  const dayValue = (date: Date) => new Date(date.getFullYear(), date.getMonth(), monthOnly ? 1 : date.getDate()).getTime()
  const currentAllowed = (!minDate || dayValue(currentDate) >= dayValue(minDate)) && (!maxDate || dayValue(currentDate) <= dayValue(maxDate))
  const months = Array.from({length: 12}, (_, month) => format(new Date(2026, month, 1), 'MMMM', {locale: tr}))
  const firstYear = minDate?.getFullYear() ?? Math.min(2000, selected?.getFullYear() ?? 2000)
  const lastYear = maxDate?.getFullYear() ?? Math.max(today.getFullYear() + 5, selected?.getFullYear() ?? 0)
  const years = Array.from({length: Math.max(1, lastYear - firstYear + 1)}, (_, index) => firstYear + index)
  return <div ref={field} className={`edu-date-control${monthOnly ? ' is-month' : ''}${disabled ? ' is-disabled' : ''}`}>
    <DatePicker ref={picker} id={id} selected={selected} onChange={onChange} disabled={disabled}
      minDate={minDate} maxDate={maxDate} locale={tr} calendarStartDay={1}
      formatWeekDay={day => ({Pazartesi: 'Pzt', Salı: 'Sal', Çarşamba: 'Çar', Perşembe: 'Per', Cuma: 'Cum', Cumartesi: 'Cmt', Pazar: 'Paz'}[day] || day.slice(0, 3))}
      chooseDayAriaLabelPrefix='Seç' disabledDayAriaLabelPrefix='Seçilemez'
      dateFormat={monthOnly ? 'MMMM yyyy' : 'dd.MM.yyyy'} strictParsing
      placeholderText={monthOnly ? 'Ay seçin' : 'gg.aa.yyyy'} autoComplete='off'
      className='edu-date-input' wrapperClassName='edu-datepicker-wrapper'
      calendarClassName='edu-calendar' popperClassName='edu-calendar-popper'
      popperContainer={CalendarPortal} popperPlacement='bottom-start' popperProps={{strategy: 'fixed'}}
      showPopperArrow={false} showMonthYearPicker={monthOnly}
      renderMonthContent={month => months[month]}
      renderCustomHeader={({date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => <div className='edu-calendar-header'>
        <button type='button' className='edu-calendar-nav is-previous' aria-label={monthOnly ? 'Önceki yıl' : 'Önceki ay'}
          disabled={monthOnly ? date.getFullYear() <= firstYear : prevMonthButtonDisabled}
          onClick={monthOnly ? () => changeYear(date.getFullYear() - 1) : decreaseMonth}><UiIcon name='arrow' size={17}/></button>
        <div className='edu-calendar-heading'>
          {!monthOnly && <select aria-label='Takvim ayı' value={date.getMonth()} onChange={event => changeMonth(Number(event.target.value))}>{months.map((month, index) => <option key={month} value={index}>{month}</option>)}</select>}
          <select aria-label='Takvim yılı' value={date.getFullYear()} onChange={event => changeYear(Number(event.target.value))}>{years.map(year => <option key={year} value={year}>{year}</option>)}</select>
        </div>
        <button type='button' className='edu-calendar-nav' aria-label={monthOnly ? 'Sonraki yıl' : 'Sonraki ay'}
          disabled={monthOnly ? date.getFullYear() >= lastYear : nextMonthButtonDisabled}
          onClick={monthOnly ? () => changeYear(date.getFullYear() + 1) : increaseMonth}><UiIcon name='arrow' size={17}/></button>
      </div>}>
      <div className='edu-calendar-footer'><span>{selected ? format(selected, monthOnly ? 'MMMM yyyy' : 'd MMMM yyyy', {locale: tr}) : 'Tarih seçin'}</span><button type='button' disabled={!currentAllowed} onClick={() => {onChange(currentDate); picker.current?.setOpen(false)}}>{monthOnly ? 'Bu ay' : 'Bugün'}</button></div>
    </DatePicker>
    <UiIcon name='calendar' size={18}/>
  </div>
}
